<template>
  <div>
    <HrPageLayout
      title="Upload Documents"
      subtitle="Upload and manage your personal documents"
      icon="📤"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Documents', route: '/employee-documents-view' },
        { label: 'Upload' }
      ]"
    >
      <div class="employee-documents-upload-content">
        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading...</p>
        </div>

        <template v-else>
          <!-- Stats -->
          <div class="hr-metrics-grid hr-mb-4">
            <div class="hr-metric-card gradient-1">
              <div class="hr-metric-icon"><i class="bx bxs-file-doc"></i></div>
              <p class="hr-metric-label">Total Documents</p>
              <h2 class="hr-metric-value">{{ stats.totalDocuments }}</h2>
            </div>
            <div class="hr-metric-card gradient-2">
              <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
              <p class="hr-metric-label">Verified</p>
              <h2 class="hr-metric-value">{{ stats.verifiedDocuments }}</h2>
            </div>
            <div class="hr-metric-card gradient-4">
              <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
              <p class="hr-metric-label">Pending</p>
              <h2 class="hr-metric-value">{{ stats.pendingDocuments }}</h2>
            </div>
            <div class="hr-metric-card gradient-3">
              <div class="hr-metric-icon"><i class="bx bx-hdd"></i></div>
              <p class="hr-metric-label">Storage Used</p>
              <h2 class="hr-metric-value">{{ stats.totalSizeMB }} MB</h2>
              <p class="hr-metric-subtext">of {{ stats.storageLimitMB }} MB</p>
            </div>
          </div>

          <!-- Storage Progress -->
          <div class="hr-chart-card hr-mb-4">
            <div class="hr-chart-body">
              <div class="hr-flex-between hr-mb-2">
                <h6 class="hr-mb-0">Storage Usage</h6>
                <span class="hr-badge" :class="getStorageBadgeClass()">{{ stats.storageUsedPercent }}%</span>
              </div>
              <div class="doc-progress-bar">
                <div class="doc-progress-fill" :class="getStorageProgressClass()"
                     :style="{ width: stats.storageUsedPercent + '%' }">
                  {{ stats.totalSizeMB }} MB / {{ stats.storageLimitMB }} MB
                </div>
              </div>
            </div>
          </div>

          <div class="doc-upload-layout">
            <!-- Upload Form -->
            <div class="doc-upload-main">
              <div class="hr-chart-card">
                <div class="hr-chart-header">
                  <h5 class="hr-chart-title"><i class="bx bx-upload"></i> Upload New Document</h5>
                </div>
                <div class="hr-chart-body">
                  <form @submit.prevent="uploadDocument" class="hr-form-grid">
                    <div class="hr-filter-group span-half">
                      <label class="hr-form-label">Document Type <span class="hr-text-danger">*</span></label>
                      <select v-model="uploadForm.documentType" class="hr-select" required>
                        <option value="">Select document type</option>
                        <option value="Academic Certificate">Academic Certificate</option>
                        <option value="Professional Certificate">Professional Certificate</option>
                        <option value="CV/Resume">CV/Resume</option>
                        <option value="ID">National ID</option>
                        <option value="Passport">Passport</option>
                        <option value="Contract">Employment Contract</option>
                        <option value="Letter">Letter</option>
                        <option value="Medical">Medical Document</option>
                        <option value="Tax">Tax Document</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div class="hr-filter-group span-half">
                      <label class="hr-form-label">Document Name <span class="hr-text-danger">*</span></label>
                      <input v-model="uploadForm.documentName" type="text" class="hr-input"
                             placeholder="E.g., Bachelor's Degree Certificate" required>
                    </div>
                    <div class="hr-filter-group span-half">
                      <label class="hr-form-label">Document Number (Optional)</label>
                      <input v-model="uploadForm.documentNumber" type="text" class="hr-input"
                             placeholder="E.g., Certificate #12345">
                    </div>
                    <div class="hr-filter-group span-half">
                      <label class="hr-form-label">Valid Until (Optional)</label>
                      <input v-model="uploadForm.validTill" type="date" class="hr-input">
                    </div>
                    <div class="hr-filter-group span-full">
                      <label class="hr-form-label">Select File <span class="hr-text-danger">*</span></label>
                      <div class="doc-upload-area" :class="{ 'drag-over': isDragOver }"
                           @drop.prevent="handleDrop"
                           @dragover.prevent="isDragOver = true"
                           @dragleave="isDragOver = false"
                           @click="$refs.fileInput?.click()">
                        <input ref="fileInput" type="file" @change="handleFileSelect"
                               accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style="display: none">
                        <div v-if="!uploadForm.file" class="doc-upload-placeholder">
                          <i class="bx bx-cloud-upload doc-upload-icon"></i>
                          <h5>Drop file here or click to browse</h5>
                          <p class="hr-text-muted hr-mb-0">Supported formats: PDF, JPG, PNG, DOC, DOCX (Max: 5MB)</p>
                        </div>
                        <div v-else class="doc-selected-file">
                          <div class="hr-flex-between">
                            <div class="doc-file-info">
                              <i :class="getFileIcon(uploadForm.file.type)" class="doc-file-icon"
                                 :style="{ color: getFileColor(uploadForm.file.type) }"></i>
                              <div>
                                <h6 class="hr-mb-1">{{ uploadForm.file.name }}</h6>
                                <p class="hr-text-muted hr-mb-0 hr-small">{{ formatFileSize(uploadForm.file.size) }}</p>
                              </div>
                            </div>
                            <button type="button" @click.stop="removeFile" class="hr-btn hr-btn-sm hr-btn-danger">
                              <i class="bx bx-x"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="hr-filter-group span-full">
                      <div class="hr-alert hr-alert-info">
                        <i class="bx bx-info-circle"></i>
                        <strong>Important:</strong> Uploaded documents will be reviewed by HR before verification. Ensure all information is accurate and files are clear and legible.
                      </div>
                    </div>
                    <div class="hr-filter-group span-full hr-flex-end">
                      <button type="button" class="hr-btn hr-btn-secondary" @click="resetForm">
                        <i class="bx bx-refresh"></i> Reset
                      </button>
                      <button type="submit" class="hr-btn hr-btn-primary" :disabled="uploading || !stats.canUpload">
                        <span v-if="uploading" class="hr-spinner small"></span>
                        <i v-else class="bx bx-upload"></i>
                        {{ uploading ? 'Uploading...' : 'Upload Document' }}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <!-- Sidebar: Recent Uploads & Quick Actions -->
            <div class="doc-upload-sidebar">
              <div class="hr-chart-card hr-mb-4">
                <div class="hr-chart-header">
                  <h5 class="hr-chart-title"><i class="bx bx-time-five"></i> Recent Uploads</h5>
                </div>
                <div class="hr-chart-body">
                  <div v-if="recentUploads.length" class="doc-timeline">
                    <div v-for="(upload, index) in recentUploads" :key="index" class="doc-timeline-item">
                      <div class="doc-timeline-icon">
                        <i :class="getFileIcon(getFileTypeFromName(upload.name))"></i>
                      </div>
                      <div class="doc-timeline-content">
                        <h6 class="hr-mb-1">{{ upload.name }}</h6>
                        <span class="hr-badge hr-badge-primary hr-mb-1">{{ upload.documentType }}</span>
                        <p class="hr-text-muted hr-mb-1 hr-small">
                          <i class="bx bx-time"></i> {{ upload.upload_date_formatted }}
                        </p>
                        <span v-if="upload.isVerified === '1'" class="hr-badge hr-badge-success">
                          <i class="bx bx-check"></i> Verified
                        </span>
                        <span v-else class="hr-badge hr-badge-warning">
                          <i class="bx bx-time-five"></i> Pending
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="hr-text-center hr-text-muted hr-py-4">
                    <i class="bx bx-file" style="font-size: 48px;"></i>
                    <p class="hr-mt-2 hr-mb-0">No recent uploads</p>
                  </div>
                </div>
              </div>

              <div class="hr-chart-card">
                <div class="hr-chart-header">
                  <h5 class="hr-chart-title"><i class="bx bx-bolt"></i> Quick Actions</h5>
                </div>
                <div class="hr-chart-body">
                  <div class="doc-quick-actions">
                    <button class="hr-btn hr-btn-outline hr-btn-block" @click="goTo('employee-documents-view')">
                      <i class="bx bx-file"></i> View All Documents
                    </button>
                    <button class="hr-btn hr-btn-outline hr-btn-block" @click="goTo('employee-documents-view')">
                      <i class="bx bx-help-circle"></i> Request Document from HR
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeDocumentsUpload",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      uploading: false,
      isDragOver: false,
      stats: {
        totalDocuments: 0,
        verifiedDocuments: 0,
        pendingDocuments: 0,
        totalSizeMB: 0,
        storageLimitMB: 50,
        storageUsedPercent: 0,
        canUpload: true
      },
      uploadForm: {
        documentType: '',
        documentName: '',
        documentNumber: '',
        validTill: '',
        file: null
      },
      recentUploads: []
    };
  },
  mounted() {
    this.loadStats();
    this.loadRecentUploads();
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeUploadStats");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.stats = res.data.data;
        }
      } catch (e) {
        console.error("Failed to load stats:", e);
      } finally {
        this.loading = false;
      }
    },
    async loadRecentUploads() {
      try {
        const data = new FormData();
        data.append("function", "getRecentUploads");
        data.append("limit", "5");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.recentUploads = res.data.data?.recentUploads || [];
        }
      } catch (e) {
        console.error("Failed to load recent uploads:", e);
      }
    },
    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.validateAndSetFile(file);
    },
    handleDrop(e) {
      this.isDragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) this.validateAndSetFile(file);
    },
    validateAndSetFile(file) {
      if (file.size > 5 * 1024 * 1024) {
        this.$toast?.error("File size must be less than 5MB") || alert("File size must be less than 5MB");
        return;
      }
      const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowed.includes(file.type)) {
        this.$toast?.error("Only PDF, JPG, PNG, DOC, and DOCX files are allowed") || alert("Invalid file type");
        return;
      }
      this.uploadForm.file = file;
    },
    removeFile() {
      this.uploadForm.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async uploadDocument() {
      if (!this.uploadForm.file) {
        this.$toast?.error("Please select a file") || alert("Please select a file");
        return;
      }
      if (!this.stats.canUpload) {
        this.$toast?.error("Storage limit exceeded. Please delete some documents first.") || alert("Storage limit exceeded");
        return;
      }
      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append("function", "uploadEmployeeDocument");
        formData.append("file", this.uploadForm.file);
        formData.append("documentType", this.uploadForm.documentType);
        formData.append("documentName", this.uploadForm.documentName);
        formData.append("documentNumber", this.uploadForm.documentNumber);
        formData.append("validTill", this.uploadForm.validTill);
        const res = await execute(formData, authUrl);
        if (res?.data?.success) {
          this.$toast?.success("Document uploaded successfully!");
          this.resetForm();
          this.loadStats();
          this.loadRecentUploads();
        } else {
          throw new Error(res?.data?.message || 'Upload failed');
        }
      } catch (error) {
        this.$toast?.error("Upload failed: " + error.message) || alert("Upload failed: " + error.message);
      } finally {
        this.uploading = false;
      }
    },
    resetForm() {
      this.uploadForm = { documentType: '', documentName: '', documentNumber: '', validTill: '', file: null };
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    getStorageBadgeClass() {
      const p = this.stats.storageUsedPercent;
      if (p >= 90) return 'hr-badge-danger';
      if (p >= 70) return 'hr-badge-warning';
      return 'hr-badge-success';
    },
    getStorageProgressClass() {
      const p = this.stats.storageUsedPercent;
      if (p >= 90) return 'doc-progress-danger';
      if (p >= 70) return 'doc-progress-warning';
      return 'doc-progress-success';
    },
    getFileIcon(fileType) {
      const t = fileType?.toLowerCase() || '';
      if (t.includes('pdf')) return 'bx bxs-file-pdf';
      if (t.includes('doc')) return 'bx bxs-file-doc';
      if (t.includes('xls') || t.includes('sheet')) return 'bx bxs-file';
      if (t.includes('image') || t.includes('jpg') || t.includes('png')) return 'bx bxs-file-image';
      return 'bx bx-file';
    },
    getFileColor(fileType) {
      const t = fileType?.toLowerCase() || '';
      if (t.includes('pdf')) return '#d32f2f';
      if (t.includes('doc')) return '#2196f3';
      if (t.includes('image')) return '#9c27b0';
      return '#607d8b';
    },
    getFileTypeFromName(fileName) {
      const ext = (fileName || '').split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'application/pdf';
      if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
      if (ext === 'png') return 'image/png';
      if (ext === 'doc') return 'application/msword';
      if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      return 'application/octet-stream';
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
    },
    goTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route.startsWith('/') ? route : '/' + route);
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.doc-upload-layout { display: grid; grid-template-columns: 1fr 340px; gap: 24px; }
@media (max-width: 992px) { .doc-upload-layout { grid-template-columns: 1fr; } }
.doc-upload-main { min-width: 0; }
.doc-progress-bar { height: 24px; background: #e2e8f0; border-radius: 12px; overflow: hidden; }
.doc-progress-fill { height: 100%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; color: #fff; transition: width 0.3s; }
.doc-progress-success { background: linear-gradient(90deg, #10b981, #34d399); }
.doc-progress-warning { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.doc-progress-danger { background: linear-gradient(90deg, #ef4444, #f87171); }
.doc-upload-area { border: 2px dashed #e2e8f0; border-radius: 12px; padding: 32px; cursor: pointer; transition: all 0.3s; background: #f8fafc; }
.doc-upload-area:hover { border-color: #059669; background: #ecfdf5; }
.doc-upload-area.drag-over { border-color: #059669; background: #d1fae5; }
.doc-upload-placeholder { text-align: center; }
.doc-upload-icon { font-size: 48px; color: #059669; margin-bottom: 12px; display: block; }
.doc-selected-file { background: #fff; border-radius: 8px; padding: 16px; }
.doc-file-info { display: flex; align-items: center; gap: 12px; }
.doc-file-icon { font-size: 36px; }
.doc-timeline { display: flex; flex-direction: column; gap: 16px; }
.doc-timeline-item { display: flex; gap: 12px; padding-left: 20px; border-left: 2px solid #e2e8f0; position: relative; }
.doc-timeline-icon { position: absolute; left: -13px; width: 24px; height: 24px; background: #fff; border: 2px solid #e2e8f0; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; }
.doc-timeline-content { flex: 1; }
.doc-quick-actions { display: flex; flex-direction: column; gap: 12px; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; }
.hr-flex-end { display: flex; justify-content: flex-end; gap: 12px; }
.hr-btn-block { width: 100%; justify-content: center; }
.hr-btn-outline { background: transparent; border: 1px solid #e2e8f0; color: #475569; }
.hr-btn-outline:hover { background: #f8fafc; border-color: #059669; color: #059669; }
.hr-alert { padding: 12px 16px; border-radius: 8px; display: flex; gap: 8px; align-items: flex-start; }
.hr-alert-info { background: #eff6ff; border-left: 4px solid #3b82f6; }
</style>
